import { useEffect, useRef } from "react";

// Makes the phone's Back (browser back, or Android's hardware back -- with no
// backButton listener registered, Capacitor's App plugin falls back to webview
// history) close an in-page overlay instead of leaving the Partners screen.
//
// While `open`, one history entry is pushed on top of the current one, at the
// same URL. Back pops it, which closes the overlay. Closing the overlay any
// other way (its own back arrow) removes the entry again, so history is left
// exactly as it was.

const MARKER = "partnersMobileOverlay";

// Counts history.back() calls this hook made itself, so the popstate they
// cause isn't mistaken for the user pressing Back. Registered once, before any
// hook listener, so it always runs first.
let pendingSelfBacks = 0;
let lastPopWasSelf = false;
// Pushes waiting for our own pending back() to land. history.back() is
// asynchronous, and how it interleaves with a pushState issued meanwhile
// (closing one detail page and opening another straight away) is not
// reliable -- so a new entry is only pushed once the pop has happened.
let afterSelfPops = [];
if (typeof window !== "undefined") {
  window.addEventListener("popstate", () => {
    lastPopWasSelf = pendingSelfBacks > 0;
    if (!lastPopWasSelf) return;
    pendingSelfBacks -= 1;
    if (pendingSelfBacks === 0) {
      const queued = afterSelfPops;
      afterSelfPops = [];
      queued.forEach((push) => push());
    }
  });
}

export default function useBackToClose(open, onClose) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    let pushed = false;
    // Keep React Router's own state (its key / idx) on the new entry, so its
    // index bookkeeping stays consistent; only the marker is added.
    const push = () => {
      if (cancelled) return;
      window.history.pushState({ ...(window.history.state || {}), [MARKER]: true }, "");
      pushed = true;
    };
    if (pendingSelfBacks > 0) afterSelfPops.push(push);
    else push();

    let poppedByUser = false;
    const onPop = () => {
      if (lastPopWasSelf || !pushed) return;
      poppedByUser = true;
      onCloseRef.current();
    };
    window.addEventListener("popstate", onPop);

    return () => {
      cancelled = true;
      window.removeEventListener("popstate", onPop);
      // Closed some other way while our entry is still on top: remove it.
      // If the user navigated elsewhere meanwhile, the top entry isn't ours
      // and is left alone.
      if (pushed && !poppedByUser && window.history.state?.[MARKER]) {
        pendingSelfBacks += 1;
        window.history.back();
      }
    };
  }, [open]);
}
